/**
 * 暖棕摩卡模板 - 注册页
 * 
 * 设计理念：宽幅专业注册流程，与居中卡片式完全区分
 * - 顶部深棕焦糖 Banner 区域展示品牌信息
 * - 宽幅白色卡片，表单字段双列排布（移动端自适应单列）
 * - 强调步骤感和专业感
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useSiteConfig } from "@/context/SiteConfigContext";
import request, { getSinglePage } from "@/utils/api";

// API 基础 URL
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

// 注册字段类型
export type FieldType = "text" | "textarea" | "select" | "radio" | "checkbox" | "password" | "number" | "date" | "file" | "image";

export interface RegisterField {
  field_name: string;
  field_label: string;
  field_type: FieldType;
  placeholder: string;
  hint?: string;
  options?: string[];
  is_required: boolean;
  is_enabled: boolean;
  sort_order: number;
}

// 获取当前站点ID
const getSiteId = () => localStorage.getItem('current_site_id') || '';

// 获取注册配置
async function getRegisterConfig(siteId: string): Promise<{ fields: RegisterField[]; sms_enabled?: boolean }> {
  return request<{ fields: RegisterField[]; sms_enabled?: boolean }>(`/register/config`, {
    params: { site_id: siteId },
  });
}

// 注册参数接口
export interface RegisterParams {
  username: string;
  password: string;
  site_id: string;
  phone?: string;
  sms_code?: string;
  [key: string]: string | undefined;
}

// 注册函数
async function registerApi(params: RegisterParams) {
  return request("/auth/register", {
    method: "POST",
    body: JSON.stringify(params),
  });
}

// 发送短信验证码
async function sendSmsCode(siteId: string, phone: string): Promise<void> {
  return request("/register/send-code", {
    method: "POST",
    params: { site_id: siteId },
    body: { phone },
  });
}

// 文件上传函数
async function uploadFile(file: File): Promise<{ url: string }> {
  const formData = new FormData();
  formData.append("file", file);
  return request("/upload", {
    method: "POST",
    body: formData,
  });
}

export default function MochaRegisterPage() {
  const [fields, setFields] = useState<RegisterField[]>([]);
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({});
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);
  const [uploadingFields, setUploadingFields] = useState<Record<string, boolean>>({});
  const { register } = useAuth();
  const navigate = useNavigate();
  const formDataRef = useRef<Record<string, string>>({});
  const { siteConfig } = useSiteConfig();

  const siteLogo = siteConfig?.logo || "https://public.readdy.ai/ai/img_res/1ebe5b90-bc3d-41ad-9f51-693742fb28f5.png";
  const siteDisplayName = siteConfig?.site_name || siteConfig?.name || "商服汇";

  // 协议弹窗相关
  const [agreementModal, setAgreementModal] = useState<{ show: boolean; title: string; content: string; loading: boolean }>({
    show: false, title: "", content: "", loading: false,
  });

  const openAgreement = useCallback(async (slug: string, title: string) => {
    setAgreementModal({ show: true, title, content: "", loading: true });
    try {
      const page = await getSinglePage(slug);
      setAgreementModal({
        show: true,
        title: page?.title || title,
        content: page?.content || "暂无内容",
        loading: false,
      });
    } catch {
      setAgreementModal({ show: true, title, content: "加载失败，请稍后重试", loading: false });
    }
  }, []);

  const closeAgreement = useCallback(() => {
    setAgreementModal({ show: false, title: "", content: "", loading: false });
  }, []);

  // 短信验证相关
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [smsCountdown, setSmsCountdown] = useState(0);
  const [smsLoading, setSmsLoading] = useState(false);

  // 加载注册配置
  useEffect(() => {
    async function loadConfig() {
      try {
        const config = await getRegisterConfig(getSiteId());
        const enabledFields = config.fields.filter((f) => f.is_enabled);

        const initialData: Record<string, string> = {};
        enabledFields.forEach((field) => {
          initialData[field.field_name] = "";
        });

        if (config.sms_enabled) {
          initialData['phone'] = '';
          initialData['sms_code'] = '';
          setSmsEnabled(true);
        }

        setFields(enabledFields);
        setFormData(initialData);
        formDataRef.current = initialData;
      } catch (err) {
        console.error("加载注册配置失败:", err);
        setError("加载表单配置失败，请刷新重试");
      } finally {
        setIsLoadingConfig(false);
      }
    }
    loadConfig();
  }, []);

  // 短信倒计时
  useEffect(() => {
    if (smsCountdown > 0) {
      const timer = setTimeout(() => setSmsCountdown(smsCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [smsCountdown]);

  // 发送短信验证码
  const handleSendSmsCode = useCallback(async () => {
    const phone = formDataRef.current['phone'] || '';

    if (!phone) {
      setError("请先输入手机号");
      return;
    }

    if (!/^1[3-9]\d{9}$/.test(phone)) {
      setError("手机号格式不正确");
      return;
    }

    try {
      setSmsLoading(true);
      await sendSmsCode(getSiteId(), phone);
      setSmsCountdown(60);
      setError("");
    } catch (err: any) {
      setError(err.message || "短信发送失败，请重试");
    } finally {
      setSmsLoading(false);
    }
  }, []);

  // 处理输入变化
  const handleChange = useCallback((fieldName: string, value: string) => {
    setFormData((prev) => {
      const newData = { ...prev, [fieldName]: value };
      formDataRef.current = newData;
      return newData;
    });
  }, []);

  // 切换密码显示
  const togglePasswordVisibility = useCallback((fieldName: string) => {
    setShowPasswords((prev) => ({ ...prev, [fieldName]: !prev[fieldName] }));
  }, []);

  // 处理文件上传
  const handleFileUpload = useCallback(async (fieldName: string, file: File) => {
    try {
      setUploadingFields((prev) => ({ ...prev, [fieldName]: true }));
      const result = await uploadFile(file);
      handleChange(fieldName, result.url);
    } catch (err) {
      console.error("文件上传失败:", err);
      alert("文件上传失败，请重试");
    } finally {
      setUploadingFields((prev) => ({ ...prev, [fieldName]: false }));
    }
  }, [handleChange]);

  // 获取图标
  const getFieldIcon = (fieldType: FieldType) => {
    switch (fieldType) {
      case "text":
      case "number":
        return "ri-edit-line";
      case "textarea":
        return "ri-file-text-line";
      case "select":
      case "radio":
      case "checkbox":
        return "ri-list-check";
      case "password":
        return "ri-lock-line";
      case "date":
        return "ri-calendar-line";
      case "file":
      case "image":
        return "ri-image-add-line";
      default:
        return "ri-edit-line";
    }
  };

  // 判断字段是否为宽字段（应占满行）
  const isWideField = (field: RegisterField) => {
    return ["textarea", "file", "image", "radio", "checkbox"].includes(field.field_type);
  };

  // 渲染字段
  const renderField = (field: RegisterField) => {
    const value = formDataRef.current[field.field_name] || "";
    const isPassword = field.field_type === "password";
    const showPassword = showPasswords[field.field_name] || false;
    const baseInputClass = `w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C77B3F]/50 focus:border-transparent transition-all`;

    switch (field.field_type) {
      case "textarea":
        return (
          <textarea
            key={field.field_name}
            value={value}
            onChange={(e) => handleChange(field.field_name, e.target.value)}
            placeholder={field.placeholder || field.hint || `请输入${field.field_label}`}
            rows={3}
            className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C77B3F]/50 focus:border-transparent resize-none"
          />
        );

      case "select":
        return (
          <select
            key={field.field_name}
            value={value}
            onChange={(e) => handleChange(field.field_name, e.target.value)}
            className={baseInputClass}
          >
            <option value="">{field.placeholder || `请选择${field.field_label}`}</option>
            {field.options?.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        );

      case "radio":
        return (
          <div key={field.field_name} className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
            {field.options?.map((option) => (
              <label
                key={option}
                className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all ${
                  value === option
                    ? "border-[#C77B3F]/50 bg-[#C77B3F]/5 ring-1 ring-[#C77B3F]/20"
                    : "border-gray-200 bg-white hover:bg-gray-50"
                }`}
              >
                <input
                  type="radio"
                  name={field.field_name}
                  value={option}
                  checked={value === option}
                  onChange={(e) => handleChange(field.field_name, e.target.value)}
                  className="w-4 h-4 text-[#C77B3F]"
                />
                <span className="text-sm">{option}</span>
              </label>
            ))}
          </div>
        );

      case "checkbox":
        return (
          <div key={field.field_name} className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
            {field.options?.map((option) => (
              <label
                key={option}
                className="flex items-center gap-2 p-3 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <input
                  type="checkbox"
                  name={field.field_name}
                  value={option}
                  checked={value.split(",").includes(option)}
                  onChange={(e) => {
                    const currentValues = value ? value.split(",") : [];
                    const newValues = e.target.checked
                      ? [...currentValues, option]
                      : currentValues.filter((v) => v !== option);
                    handleChange(field.field_name, newValues.join(","));
                  }}
                  className="w-4 h-4 text-[#C77B3F] rounded"
                />
                <span className="text-sm">{option}</span>
              </label>
            ))}
          </div>
        );

      case "date":
        return (
          <input
            key={field.field_name}
            type="date"
            value={value}
            onChange={(e) => handleChange(field.field_name, e.target.value)}
            className={baseInputClass}
          />
        );

      case "number":
        return (
          <input
            key={field.field_name}
            type="number"
            value={value}
            onChange={(e) => handleChange(field.field_name, e.target.value)}
            placeholder={field.placeholder || field.hint || `请输入${field.field_label}`}
            className={baseInputClass}
          />
        );

      case "file":
      case "image":
        return (
          <div key={field.field_name}>
            <label
              htmlFor={`file-${field.field_name}`}
              className="flex flex-col items-center justify-center w-full px-4 py-6 bg-white border-2 border-dashed border-gray-200 rounded-lg cursor-pointer hover:border-[#C77B3F] hover:bg-[#C77B3F]/5 transition-all"
            >
              {uploadingFields[field.field_name] ? (
                <div className="flex flex-col items-center">
                  <div className="w-8 h-8 border-4 border-[#C77B3F] border-t-transparent rounded-full animate-spin mb-2"></div>
                  <span className="text-sm text-gray-500">上传中...</span>
                </div>
              ) : value ? (
                <div className="flex flex-col items-center">
                  {field.field_type === "image" ? (
                    <img
                      src={value.startsWith('/uploads') ? value : value}
                      alt="预览"
                      className="w-24 h-24 object-cover rounded-lg mb-2"
                      onError={(e) => {
                        console.error('图片加载失败:', value);
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  ) : (
                    <i className="ri-file-check-line text-3xl text-green-500 mb-2"></i>
                  )}
                  <span className="text-sm text-gray-600">点击重新上传</span>
                </div>
              ) : (
                <div className="flex flex-col items-center">
                  <i className="ri-upload-cloud-2-line text-3xl text-gray-300 mb-2"></i>
                  <span className="text-sm text-gray-500">{field.placeholder || `点击上传${field.field_type === "image" ? "图片" : "文件"}`}</span>
                  {field.hint && <span className="text-xs text-gray-400 mt-1">{field.hint}</span>}
                </div>
              )}
            </label>
            <input
              id={`file-${field.field_name}`}
              type="file"
              accept={field.field_type === "image" ? "image/*" : "*/*"}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  handleFileUpload(field.field_name, file);
                }
              }}
              className="hidden"
            />
            {value && (
              <button
                type="button"
                onClick={() => handleChange(field.field_name, "")}
                className="mt-2 text-sm text-[#C77B3F] hover:text-[#B0672F] cursor-pointer"
              >
                清除
              </button>
            )}
          </div>
        );

      default:
        return (
          <div className="relative">
            <input
              key={field.field_name}
              type={isPassword ? (showPassword ? "text" : "password") : "text"}
              value={value}
              onChange={(e) => handleChange(field.field_name, e.target.value)}
              placeholder={field.placeholder || field.hint || `请输入${field.field_label}`}
              className={isPassword ? `${baseInputClass} pr-11` : baseInputClass}
            />
            {isPassword && (
              <button
                type="button"
                onClick={() => togglePasswordVisibility(field.field_name)}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 cursor-pointer rounded-md hover:bg-gray-100 transition-colors"
              >
                <i className={showPassword ? "ri-eye-off-line" : "ri-eye-line"} />
              </button>
            )}
          </div>
        );
    }
  };

  // 表单验证
  const validate = () => {
    const currentData = formDataRef.current;

    if (!agreed) {
      setError("请先阅读并同意注册协议和隐私政策");
      return false;
    }

    for (const field of fields) {
      if (field.is_required && !(currentData[field.field_name] || "").trim()) {
        setError(`${field.field_label}不能为空`);
        return false;
      }
    }

    if (smsEnabled) {
      const phone = currentData['phone'] || '';
      const smsCode = currentData['sms_code'] || '';

      if (!phone) { setError("请输入手机号"); return false; }
      if (!/^1[3-9]\d{9}$/.test(phone)) { setError("手机号格式不正确"); return false; }
      if (!smsCode) { setError("请输入短信验证码"); return false; }
      if (smsCode.length !== 6) { setError("短信验证码为6位数字"); return false; }
    }

    const usernameField = fields.find((f) => f.field_name === "username");
    if (usernameField && currentData.username) {
      if (!/^\w{3,20}$/.test(currentData.username)) {
        setError("用户名只能包含字母、数字和下划线，长度3-20位");
        return false;
      }
    }

    if (currentData.password && currentData.password.length < 6) {
      setError("密码长度至少6位");
      return false;
    }

    return true;
  };

  // 提交表单
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!validate()) return;

    setIsLoading(true);
    try {
      const currentData = formDataRef.current;

      const params: RegisterParams = {
        username: currentData.username,
        password: currentData.password,
        site_id: getSiteId(),
        confirm_password: currentData.confirm_password || '',
      };

      fields.forEach((field) => {
        if (!["username", "password", "nickname", "confirm_password"].includes(field.field_name)) {
          params[field.field_name] = currentData[field.field_name] ?? '';
        }
      });

      if (smsEnabled) {
        params.phone = currentData['phone'];
        params.sms_code = currentData['sms_code'];
      }

      await registerApi(params);
      navigate("/");
    } catch (err: any) {
      setError(err.message || "注册失败，请稍后重试");
    } finally {
      setIsLoading(false);
    }
  };

  // 已注册的基本字段（username, password, confirm_password）- 在双列网格外特殊处理
  const basicFields = fields.filter(f => ["username", "password", "confirm_password", "nickname"].includes(f.field_name));
  const customFields = fields.filter(f => !["username", "password", "confirm_password", "nickname"].includes(f.field_name));

  if (isLoadingConfig) {
    return (
      <div className="min-h-screen bg-[#FAF6F0] flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line animate-spin text-3xl text-[#C77B3F]" />
          <p className="mt-2 text-[#8A7B6D] text-sm">加载中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF6F0]">
      {/* ── 顶部深棕 Banner ── */}
      <div className="bg-gradient-to-r from-[#4A3525] via-[#3F2C1F] to-[#2B1D14] relative overflow-hidden">
        {/* 焦糖装饰 */}
        <div className="absolute top-0 right-0 w-72 h-72 rounded-full border border-[#C77B3F]/20 -translate-y-1/2 translate-x-1/4" />
        <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full border border-[#C77B3F]/15 translate-y-1/2 -translate-x-1/4" />
        <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-[#C77B3F]/10 rounded-full blur-3xl" />
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.8) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.8) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }} />

        <div className="relative z-10 max-w-5xl mx-auto px-6 py-12 sm:py-16">
          {/* Logo & 返回 */}
          <div className="flex items-center justify-between mb-8">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-sm text-[#C9B8A2] hover:text-white transition-colors group"
            >
              <i className="ri-arrow-left-line group-hover:-translate-x-0.5 transition-transform" />
              返回首页
            </Link>
            <Link to="/" className="flex items-center gap-2.5">
              {siteLogo && (
                <div className="w-9 h-9 rounded-lg bg-white/10 border border-[#C77B3F]/30 flex items-center justify-center p-1 overflow-hidden">
                  <img src={siteLogo} alt={siteDisplayName} className="w-full h-full object-contain brightness-0 invert" />
                </div>
              )}
              <span className="text-lg font-bold text-[#F5EBDD]">{siteDisplayName}</span>
            </Link>
          </div>

          {/* 标题 */}
          <h1 className="text-3xl sm:text-4xl font-bold text-[#F5EBDD] mb-3">创建您的账户</h1>
          <p className="text-[#C9B8A2] text-lg">
            加入{siteDisplayName}，开启高效的商业服务体验
          </p>
        </div>
      </div>

      {/* ── 表单区域 ── */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 -mt-8 pb-16">
        <div className="bg-white rounded-xl shadow-xl shadow-[#4A3525]/10 border border-[#F0E6D6] p-6 sm:p-10">
          <form onSubmit={handleSubmit}>
            {/* 基本信息区 - 双列 */}
            {basicFields.length > 0 && (
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-8 h-8 rounded-lg bg-[#C77B3F]/10 flex items-center justify-center">
                    <i className="ri-user-settings-line text-[#B0672F]" />
                  </div>
                  <h3 className="text-base font-semibold text-gray-800">基本信息</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                  {basicFields.map((field) => (
                    <div key={field.field_name} className={field.field_name === "nickname" || field.field_name === "confirm_password" ? "" : ""}>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        {field.field_label}
                        {field.is_required && <span className="text-[#C77B3F] ml-0.5">*</span>}
                      </label>
                      <div className="relative">
                        <i
                          className={`${getFieldIcon(field.field_type)} absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400`}
                        />
                        {renderField(field)}
                      </div>
                      {field.hint && (
                        <p className="mt-1 text-xs text-gray-400">{field.hint}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 自定义字段区 - 双列网格 */}
            {customFields.length > 0 && (
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-8 h-8 rounded-lg bg-[#C77B3F]/10 flex items-center justify-center">
                    <i className="ri-profile-line text-[#B0672F]" />
                  </div>
                  <h3 className="text-base font-semibold text-gray-800">补充信息</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                  {customFields.map((field) => (
                    <div key={field.field_name} className={isWideField(field) ? "sm:col-span-2" : ""}>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        {field.field_label}
                        {field.is_required && <span className="text-[#C77B3F] ml-0.5">*</span>}
                      </label>
                      <div className={`relative ${["textarea", "file", "image", "radio", "checkbox"].includes(field.field_type) ? "" : ""}`}>
                        {!["textarea", "file", "image", "radio", "checkbox"].includes(field.field_type) && (
                          <i
                            className={`${getFieldIcon(field.field_type)} absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400`}
                          />
                        )}
                        {renderField(field)}
                      </div>
                      {field.hint && (
                        <p className="mt-1 text-xs text-gray-400">{field.hint}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 短信验证码（如果启用） */}
            {smsEnabled && (
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-8 h-8 rounded-lg bg-[#C77B3F]/10 flex items-center justify-center">
                    <i className="ri-smartphone-line text-[#B0672F]" />
                  </div>
                  <h3 className="text-base font-semibold text-gray-800">手机验证</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                  {/* 手机号 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      手机号 <span className="text-[#C77B3F]">*</span>
                    </label>
                    <div className="relative">
                      <i className="ri-phone-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="tel"
                        value={formData['phone'] || ''}
                        onChange={(e) => {
                          handleChange('phone', e.target.value);
                          setError("");
                        }}
                        placeholder="请输入手机号"
                        className="w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C77B3F]/50 focus:border-transparent transition-all"
                      />
                    </div>
                  </div>

                  {/* 验证码 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      短信验证码 <span className="text-[#C77B3F]">*</span>
                    </label>
                    <div className="relative">
                      <i className="ri-shield-check-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        value={formData['sms_code'] || ''}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                          handleChange('sms_code', value);
                          setError("");
                        }}
                        placeholder="请输入6位验证码"
                        maxLength={6}
                        className="w-full pl-10 pr-24 py-3 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C77B3F]/50 focus:border-transparent tracking-widest transition-all"
                      />
                      <button
                        type="button"
                        onClick={handleSendSmsCode}
                        disabled={smsCountdown > 0 || smsLoading}
                        className="absolute right-1.5 top-1/2 -translate-y-1/2 px-3 py-1.5 text-xs bg-[#C77B3F] text-white rounded-md hover:bg-[#A8622A] disabled:bg-gray-300 disabled:text-gray-500 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        {smsLoading ? '发送中...' : smsCountdown > 0 ? `${smsCountdown}s后重发` : '获取验证码'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 错误提示 */}
            {error && (
              <div className="mb-6 p-4 bg-[#C77B3F]/10 text-[#B0672F] text-sm rounded-xl flex items-center gap-3 border border-[#C77B3F]/30">
                <i className="ri-error-warning-line shrink-0 text-lg" />
                <span>{error}</span>
              </div>
            )}

            {/* 协议勾选 */}
            <div
              className={`flex items-start gap-3 p-4 rounded-xl border transition-all cursor-pointer mb-6 ${
                agreed ? "border-[#C77B3F]/40 bg-[#C77B3F]/5" : "border-gray-200 bg-gray-50/50 hover:bg-gray-50"
              }`}
              onClick={() => setAgreed(!agreed)}
            >
              <div
                className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 mt-0.5 transition-all ${
                  agreed ? "border-[#C77B3F] bg-[#C77B3F]" : "border-gray-300 bg-white"
                }`}
              >
                {agreed && <i className="ri-check-line text-white text-xs" />}
              </div>
              <p className="text-sm text-gray-600 leading-relaxed select-none">
                我已阅读并同意
                <span
                  className="text-[#B0672F] hover:underline mx-1 cursor-pointer font-medium"
                  onClick={(e) => { e.stopPropagation(); openAgreement("register-agreement", "注册协议"); }}
                >
                  《注册协议》
                </span>
                和
                <span
                  className="text-[#B0672F] hover:underline mx-1 cursor-pointer font-medium"
                  onClick={(e) => { e.stopPropagation(); openAgreement("privacy", "隐私政策"); }}
                >
                  《隐私政策》
                </span>
              </p>
            </div>

            {/* 提交按钮 */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3.5 bg-gradient-to-r from-[#C77B3F] to-[#A8622A] text-white font-semibold rounded-xl hover:from-[#B0672F] hover:to-[#8F5422] transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-[#C77B3F]/25 hover:shadow-[#C77B3F]/40 text-base"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin" />
                  注册中...
                </span>
              ) : (
                "立即注册"
              )}
            </button>
          </form>

          {/* 底部登录链接 */}
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-500">
              已有账户？{" "}
              <Link to="/login" className="text-[#B0672F] font-semibold hover:text-[#8F5422] transition-colors underline underline-offset-2">
                立即登录
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* ── 协议弹窗 ── */}
      {agreementModal.show && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={closeAgreement}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
              <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <i className="ri-file-text-line text-[#C77B3F]" />
                {agreementModal.title}
              </h2>
              <button
                onClick={closeAgreement}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-5">
              {agreementModal.loading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="text-center">
                    <i className="ri-loader-4-line animate-spin text-2xl text-[#C77B3F]" />
                    <p className="mt-2 text-sm text-gray-400">加载中...</p>
                  </div>
                </div>
              ) : (
                <div
                  className="prose prose-sm max-w-none text-gray-600 leading-relaxed whitespace-pre-wrap"
                  dangerouslySetInnerHTML={{ __html: agreementModal.content }}
                />
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-100 shrink-0 flex justify-end">
              <button
                onClick={closeAgreement}
                className="px-6 py-2.5 bg-[#4A3525] text-white font-medium rounded-lg hover:bg-[#3A2A1D] transition-colors text-sm cursor-pointer"
              >
                已了解，关闭
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
